// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import myw from 'myWorld-base';
import 'jquery-ui';
import 'jquery-ui-touch';
import './kmlLayerEditor';
import './esriLayerEditor';
import './ogcLayerEditor';
import './genericTilesLayerEditor';

myw.DefineLayerControl = myw.Dialog.extend({
    events: {
        'keyup .text-layer-search': 'filterLayers'
    },

    options: {
        modal: true,
        autoOpen: true,
        width: 520,
        height: 'auto',
        resizable: true,
        position: { my: 'center', at: 'top+160', of: window, collision: 'fit' },
        title: '{:define_layer_title}',
        buttons: {
            Save: {
                text: '{:save_layer_btn}',
                class: 'primary-btn',
                click() {
                    this.saveLayerDefinition();
                }
            },
            Delete: {
                text: '{:delete_btn}',
                click() {
                    this.deleteLayerDefinition();
                }
            },
            Cancel: {
                text: '{:cancel_btn}',
                class: 'right',
                click() {
                    this.close();
                }
            }
        },
        open(event, ui) {
            $(this)
                .find('.text-layer-search')
                .blur();
        }
    },
    /**
     * @class Control for users to define a new layer
     * @constructs
     * @extends {Control}
     */
    initialize(layerManager, options) {
        this.setOptions(options);

        const isOwner = !options.layerDef || options.layerDef.owner == myw.currentUser.username;
        this.isNotEditable = !isOwner || myw.isNativeApp;

        this.layerManager = layerManager;
        this.dsType = options.layerDef ? options.layerDef.datasource_spec.type : 'kml';
        this.id = options.layerDef ? options.layerDef.id : null;

        //not show delete button when creating a layer
        if (!this.id) this.options.buttons = _(this.options.buttons).omit('Delete');
        if (this.isNotEditable)
            this.options.buttons = _(this.options.buttons).omit('Save', 'Delete');

        //call control constructor
        myw.Dialog.prototype.initialize.call(this, options);
    },

    render() {
        //build UI
        const maxHeight = $(window).height() - 100;
        this.options.maxHeight = maxHeight;
        this.form = this.createForm();
        const layerDef = this.options.layerDef;
        if (layerDef) {
            this.form.setValuesFrom(layerDef);
            this.options.title = `${this.msg('edit_layer_title')} ${layerDef.name}`;
        }
        this.options.contents = this.form.el;

        if (this.isNotEditable) this.form.disable();

        myw.Dialog.prototype.render.call(this);
        this.translate(this.$el);
    },

    createForm() {
        const LayerEditor = myw.layerEditors[this.dsType];
        if (!LayerEditor) throw new Error(`No class for datasource type: ${this.dsType}`);

        return new LayerEditor({
            id: this.id,
            system: this.layerManager.system,
            typeChangeCallback: this._checkForTypeChange.bind(this)
        });
    },

    saveLayerDefinition() {
        const def = this.form.getLayerDef();
        def.category = 'overlay';
        def.id = this.id;
        def.thumbnail = 'images/layers/private_layer.svg';

        this.validate(def).then(validityObj => {
            if (validityObj.isValid) {
                this.form.displayMessage(this.msg('saving'), 'alert');
                return this.layerManager
                    .savePrivateLayerDef(def)
                    .then(newDef => {
                        //make it available for the add layer control
                        this.form.displayMessage(this.msg('saved'));
                        setTimeout(() => {
                            this.close();
                            if (this.options.onChangeCallback)
                                this.options.onChangeCallback.call(undefined, newDef);
                        }, 1000);
                    })
                    .catch(reason => {
                        this.form.displayMessage(this.msg('error_saving'), 'error');
                        console.log('Error saving new layer definition', reason);
                    });
            } else {
                this.form.displayMessage(this.msg(validityObj.msg), 'error');
            }
        });
    },

    validate(def) {
        this.$el.find('.validationHighlight').removeClass('validationHighlight');

        //ENH: Get a fresh set of layers from the database to accurately validate
        return this.layerManager.getAvailableLayers().then(layers => {
            if (!this.id) {
                //Creating a new layer
                const layerName = def.name;
                const id = `${myw.currentUser.username}:${layerName}`;
                const existingNames = _.pluck(layers, 'id');
                if (!layerName.length) {
                    this.$("input[name='name']").addClass('validationHighlight');
                    return { isValid: false, msg: 'blank_name_error' };
                } else if (_.contains(existingNames, id)) {
                    this.$("input[name='name']").addClass('validationHighlight');
                    return { isValid: false, msg: 'duplicate_name_error' };
                }
            }
            const transparency = def.transparency;
            if (!transparency && transparency !== 0) {
                this.$("input[name='transparency']").addClass('validationHighlight');
                return { isValid: false, msg: 'blank_transparency_error' };
            }
            if (!$.isNumeric(transparency) || transparency < 0 || transparency > 100) {
                this.$("input[name='transparency']").addClass('validationHighlight');
                return { isValid: false, msg: 'invalid_percent_value' };
            }
            return { isValid: true };
        });
    },

    deleteLayerDefinition() {
        const self = this;
        new myw.Dialog({
            title: this.msg('confirm_delete_title'),
            contents: this.msg('confirm_delete_message'),
            destroyOnClose: true,
            buttons: {
                OK: {
                    text: this.msg('ok_btn'),
                    click() {
                        this.close();
                        self._confirmedDelete();
                    }
                },
                Cancel: {
                    text: this.msg('cancel_btn'),
                    class: 'right',
                    click() {
                        this.close();
                    }
                }
            }
        });
    },

    async _confirmedDelete() {
        const isDeleted = await this.layerManager.deletePrivateLayerDef(this.id).catch(err => {
            this.form.displayMessage(
                this.msg('not_deleted', { title: this.options.title }),
                'error'
            );
        });

        if (isDeleted) {
            this.form.displayMessage(this.msg('deleted', { title: this.options.title }));
            setTimeout(() => {
                if (this.options.onChangeCallback)
                    this.options.onChangeCallback.call(undefined, null);
            }, 1000);
        }
    },

    async _checkForTypeChange() {
        const currentDsType = this.form.getValue('dsType');
        const changed = this.dsType != currentDsType;
        if (changed) {
            this.dsType = currentDsType;
            const values = this.form.getValues();
            await this.render();
            this.form.setValues(values);
        }
    }
});

export default myw.DefineLayerControl;
